import { merge } from 'lodash';
import { FormGroup, FormControl } from '@angular/forms';
import { Component, OnInit, Output, EventEmitter, ViewChild, TemplateRef, Input, OnDestroy } from '@angular/core';
import { Logger } from '@app/core';
import {
  TramiteBasico,
  Microproceso,
  Tramite,
  TypeProcess
} from '@app/shared';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs/Subscription';
import { ProcessAutomationService, StorageService } from '../shared';
import { FlowTitle } from '@app/shared/title';
import { SelectTramiteComponent } from '@app/shared/select-tramite/select-tramite.component';
import { HelperFlowService } from '@app/shared/services/helper-flow.service';
import { NotifierService } from '@app/shared/services/notifier.service';

const log = new Logger('FlowNavigatorComponent');

interface InformacionFlujo {
  id: number;
  nombre: string;
  descripcion: string;
  proceso_externo: boolean;
}

@Component({
  selector: 'iw-flow-navigator',
  templateUrl: './flow-navigator.component.html',
  styleUrls: ['./flow-navigator.component.scss'],
  providers: [ProcessAutomationService, StorageService]
})
export class FlowNavigatorComponent implements OnInit, OnDestroy {
  // Contiene el tipo de proceso que se esta renderizando
  @Input() typeProcessCurrent: TypeProcess;
  // Contiene la informacion del titulo del proceso actual
  @Input() configFlowTitleCurrent: Partial<FlowTitle>;
  // Fujo del proceso principal
  @Input() set currentActivities(flow: TramiteBasico | Microproceso) {
      this._currentActivities = flow;
  }
  // Entrada del historial de flujos
  @Input() flowsHistory: number[] = [];
  // Define el tipo de proceso a renderizar en el navegar de seleccion de tarea asociada
  typeProcess: TypeProcess;
  // Define cuando se esta cargando un flujo
  isLoadingFlow = false;
  // Define si muestra o no el contenedor del flujo
  viewFlow = false;
  // Historial de consulta del tramite seleccionado
  // Contiene el historial de flujos
  historyFlow: number[] = [];
  // Configuración para generar el título del flujo
  configFlowTitle: Partial<FlowTitle>;
  // Formulario que define flujo principal
  formFlow: FormGroup = new FormGroup({
    flowCurrent: new FormControl(false)
  });
  // Informacion del flujo actual consutado
  infoFlowCurrent: InformacionFlujo;
  // Suscripción a las notificaciones de otros componentes
  private notifications: Subscription;
  private _currentActivities: TramiteBasico | Microproceso;
  // Flujo actual consultado
  private _flow: TramiteBasico | Microproceso;
  // Referencia al componente de seleccion de tramites
  @ViewChild('selectTypesTpl') private selectTypesTpl: TemplateRef<SelectTramiteComponent>;
  // Emite la cancelacion de la accion
  @Output() cancelAction: EventEmitter<boolean> = new EventEmitter();
  @Output() goBack: EventEmitter<boolean> = new EventEmitter();

  constructor(
    private toastr: ToastrService,
    private hfs: HelperFlowService,
    private pas: ProcessAutomationService,
    private ns: NotifierService,
    public ss: StorageService,
  ) { }

  ngOnInit() {
    this.ss.saveFlows = false;
    this.ss.typeProcess = this.typeProcessCurrent;
    if (this.flowsHistory && this.flowsHistory.length) {
      this.flowsHistory.forEach(h => {
        this.historyFlow.push(h);
      });
    }
    this.notificationsComponent();
    this.loadFlow(true);
    this.formFlow.controls['flowCurrent'].valueChanges.subscribe(data => {
      if (!data) {
        this.historyFlow = [];
        if (this.flowsHistory && this.flowsHistory.length) {
          this.flowsHistory.forEach(h => {
            this.historyFlow.push(h);
          });
        }
      }
      this.loadFlow(true);
    });
  }

  ngOnDestroy() {
    this.notifications.unsubscribe();
  }

  set flow (f: TramiteBasico | Microproceso) {
    if (this.historyFlow.length <= 1) {
      if (f) {
        this.infoFlowCurrent = {
          id: f.id,
          nombre: f['nombre'] ? f['nombre'] : f.descripcion,
          descripcion: f.descripcion,
          proceso_externo: this.formFlow.controls['flowCurrent'].value
        };
      } else {
        this.infoFlowCurrent = null;
      }
    }
    if (f && f.actividades && f.actividades.length) {
      f.actividades = this.hfs.refineActivities(f.actividades);
    }
    this._flow = f;
  }

  get flow (): TramiteBasico | Microproceso {
    return this._flow;
  }

  get currentActivities() {
    return this._currentActivities;
  }

  /**
   * Cancelar accion de navegar en los flujos
   *
   * @memberof FlowNavigatorComponent
   */
  actionCancel() {
    this.cancelAction.emit(true);
  }

  /**
   * Consulta el flujo seleccionado
   *
   * @param {number} idTramite
   * @memberof FlowNavigatorComponent
   */
  async loadProcessFlow(idTramite: number) {
    if (idTramite > 0) {
      log.debug('id_proceso', idTramite);
      this.isLoadingFlow = true;
      this.viewFlow = true;
      let flow: Tramite;
      if (!this.formFlow.controls['flowCurrent'].value) {
        flow = <Tramite>this.ss.getFromLocal([idTramite], 1);
      }
      if (!flow) {
        await this.pas.getDefaultTramite(idTramite)
          .then(data => {
            this.applyFlow(idTramite, data);
          })
          .catch((err) => {
            this.isLoadingFlow = false;
            log.debug(err);
          });
      } else {
        this.applyFlow(idTramite, flow);
      }
    } else {
      this.historyFlow = [];
      this.viewFlow = false;
      this.isLoadingFlow = false;
      this.flow = null;
      this.configFlowTitle = null;
    }
  }

  /**
   * Consulta el flujo de la secundaria seleccionada
   *
   * @param {number} idTramite
   * @memberof FlowNavigatorComponent
   */
  async loadSecundaryProcessFlow(idTramite: number) {
    if (idTramite > 0) {
      log.debug('id_proceso', idTramite);
      this.isLoadingFlow = true;
      this.viewFlow = true;
      let flow: Tramite;
      if (!this.formFlow.controls['flowCurrent'].value) {
        flow = <Tramite>this.ss.getFromLocal([idTramite], 1);
      }
      if (!flow) {
        await this.pas.getDefaultSecondaryProcess(idTramite)
          .then(data => {
            this.applyFlow(idTramite, data);
          })
          .catch((err) => {
            this.isLoadingFlow = false;
            log.debug(err);
          });
      } else {
        this.applyFlow(idTramite, flow);
      }
    } else {
      this.historyFlow = [];
      this.viewFlow = false;
      this.isLoadingFlow = false;
      this.flow = null;
      this.configFlowTitle = null;
    }
  }

  /**
   * Controla las acciones en los flujos dependiendo del tipo de tarea que se esta seleccionando
   *
   * @param {*} [action]
   * @param {*} [data]
   * @memberof FlowNavigatorComponent
   */
  defineAction(action?: any, data?: any) {
      this.defineActionFlowProcess(action, data);
  }

  /**
   * Carga el flujo definiendo si es el flujo principal o asociado para seleccionar la tarea ha asociar
   *
   * @private
   * @memberof FlowNavigatorComponent
   */
  private loadFlow(init: boolean = false) {
    if (!this.formFlow.controls['flowCurrent'].value) {
      this.typeProcess = this.typeProcessCurrent;
      this.flow = {} as Tramite;
      this.flow = merge(this.flow, this.currentActivities);
      if (!init) {
        this.historyFlow = [this.flow.id];
      }
      this.viewFlow = true;
      this.configFlowTitle = this.configFlowTitleCurrent;
      this.configFlowTitle.lengthFlowsHistory = this.historyFlow.length;
    } else {
      this.historyFlow = [];
      this.flow = undefined;
      this.viewFlow = false;
      this.configFlowTitle = null;
      this.typeProcess = 'process';
    }
  }

  /**
   * Controla las notificaciones del componente
   *
   * @private
   * @memberof FlowNavigatorComponent
   */
  private notificationsComponent() {
    this.notifications = this.ns.get().subscribe(async n => {
      log.debug('notificación', n);
      if (n.source === 'task' && n.instance === this.typeProcessCurrent + 'flow_navigator') {
        switch (n.action) {
          case 'runMicroProcess':
            // Accion de seleccion de un microproceso en el flujo dela tarea inicial
            this.defineActionFlowProcess('openMicroProcess', n.data.task.id);
            break;
          default:
            this.ns.set({
              source: n.source,
              eventTriggered: n.eventTriggered,
              action: n.action,
              instance: this.typeProcessCurrent,
              data: n.data
            });
            break;
        }
      }
    });
  }

  /**
   * Define las acciones a realizar en el flujo de las tareas de destino de finalizacion
   *
   * @private
   * @param {('openMicroProcess' | 'goBack')} [action='goBack']
   * @param {*} [data]
   * @memberof FlowNavigatorComponent
   */
  private defineActionFlowProcess(action: 'openMicroProcess' | 'goBack' = 'goBack', data?: any) {
    switch (action) {
      case 'openMicroProcess':
        // Abrir un microproceso en el flujo actual
        if (data) {
          if (this.formFlow.controls['flowCurrent'].value) {
            this.runMicroProcess(data);
          } else {
            if (this.typeProcessCurrent === 'secondary_process') {
              this.runMicroSecondaryProcess(data);
            } else {
              this.runMicroProcess(data);
            }
          }
        }
        break;
      default:
        // Devolverse entre la navegacion de los flujos
        if (this.historyFlow.length > 1) {
          this.historyFlow.pop();
        }
        if (this.historyFlow.length > 1) {
          if (this.formFlow.controls['flowCurrent'].value) {
            this.runMicroProcess(this.historyFlow[this.historyFlow.length - 1], true);
          } else {
            if (this.typeProcessCurrent === 'secondary_process') {
              this.runMicroSecondaryProcess(data);
            } else {
              this.runMicroProcess(data);
            }
          }
        } else {
          if (this.formFlow.controls['flowCurrent'].value) {
            this.loadProcessFlow(this.historyFlow[0]);
          } else {
            if (this.historyFlow[0] === this.currentActivities.id) {
              this.loadFlow();
            } else {
              if (this.typeProcessCurrent === 'process') {
                this.loadProcessFlow(this.historyFlow[0]);
              } else {
                this.loadSecundaryProcessFlow(this.historyFlow[0]);
              }
            }
          }
        }
        break;
    }
  }

  /**
   * Consulta el flujo de un microproceso del tramite seleccionado
   *
   * @private
   * @param {number} idMicroProcess
   * @param {boolean} [refresh=false]
   * @memberof FlowNavigatorComponent
   */
  private async runMicroProcess(idMicroProcess: number, refresh: boolean = false) {
    if (idMicroProcess) {
      this.isLoadingFlow = true;
      let flow: Microproceso;
      if (!this.formFlow.controls['flowCurrent'].value) {
        flow = <Microproceso>this.ss.getMicroProcess([idMicroProcess], true);
      }
      if (!flow) {
        await this.pas.getDefaultMicroProces(idMicroProcess)
          .then(data => {
            this.applyFlowMicroProcess(idMicroProcess, data, refresh);
          })
          .catch((err) => {
            this.isLoadingFlow = false;
            log.debug(err);
          });
      } else {
        this.applyFlowMicroProcess(idMicroProcess, flow, refresh);
      }
    }
  }

  /**
   * Consulta el flujo de un microproceso de la secundaria actual
   *
   * @private
   * @param {number} idMicroProcess
   * @param {boolean} [refresh=false]
   * @memberof FlowNavigatorComponent
   */
  private async runMicroSecondaryProcess(idMicroProcess: number, refresh: boolean = false) {
    if (idMicroProcess) {
      this.isLoadingFlow = true;
      let flow: Microproceso;
      if (!this.formFlow.controls['flowCurrent'].value) {
        flow = <Microproceso>this.ss.getMicroProcess([idMicroProcess], true);
      }
      if (!flow) {
        await this.pas.getDefaultSecondaryMicroProces(idMicroProcess)
          .then(data => {
            this.applyFlowMicroProcess(idMicroProcess, data, refresh);
          })
          .catch((err) => {
            this.isLoadingFlow = false;
            log.debug(err);
          });
      } else {
        this.applyFlowMicroProcess(idMicroProcess, flow, refresh);
      }
    }
  }

  /**
   * Aplica a la renderizacion el flujo indicado de un tramite
   *
   * @private
   * @param {number} idTramite
   * @param {Tramite} data
   * @memberof FlowNavigatorComponent
   */
  private applyFlow(idTramite: number, data: Tramite) {
    if (data.actividades && data.actividades.length) {
      this.historyFlow = [idTramite];
      log.debug('history', this.historyFlow);
      this.flow = data;
      log.debug('flow', this.flow);
      this.configFlowTitle = this.hfs.getFlowTitle(this.flow as Tramite);
      this.configFlowTitle.lengthFlowsHistory = this.historyFlow.length;
    } else {
      this.historyFlow = [];
      this.viewFlow = false;
      this.isLoadingFlow = false;
      this.flow = null;
      this.configFlowTitle = null;
      this.toastr.warning(
        'No se encuentra un flujo configurado para el proceso seleccionado.',
        'Flujo no configurado.'
      );
    }
    this.isLoadingFlow = false;
  }

  /**
   * Aplica a la renderizacion el flujo indicado de un microproceso
   *
   * @private
   * @param {number} idMicroProcess
   * @param {Microproceso} data
   * @param {boolean} [refresh=false]
   * @memberof FlowNavigatorComponent
   */
  private applyFlowMicroProcess(idMicroProcess: number, data: Microproceso, refresh: boolean = false) {
    log.debug('id_microproceso', idMicroProcess);
    if (!refresh) {
      this.historyFlow.push(idMicroProcess);
    }
    log.debug('history', this.historyFlow);
    this.flow = data;
    log.debug('flow', this.flow);
    this.isLoadingFlow = false;
    this.configFlowTitle = this.hfs.getFlowTitle(this.flow);
    this.configFlowTitle.lengthFlowsHistory = this.historyFlow.length;
  }

}



// WEBPACK FOOTER //
// ./src/app/administration/process-automation/flow-navigator/flow-navigator.component.ts